/**
 * PaquetesTuristicosManager: CRUD inline de paquetes turísticos para un prestador.
 * Se muestra dentro del formulario del prestador (AdminBeneficiosPage) sólo cuando
 * la categoría seleccionada es "Paquetes Turísticos".
 *
 * Cada paquete tiene:
 *  - id (uuid autogenerado)
 *  - titulo (obligatorio)
 *  - imagen_url (URL de la imagen principal)
 *  - descripcion_corta (subtítulo)
 *  - descripcion_larga (Markdown)
 *  - precio (texto libre: "$150.000 por persona", "USD 500", etc.)
 *  - vigencia (texto libre: "Vigente hasta 30/12/2026")
 *  - boton_tipo + boton_config (mismo sistema que los botones del prestador)
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Package,
  ArrowUp,
  ArrowDown,
  GripVertical,
} from 'lucide-react';

const generarId = () =>
  (crypto?.randomUUID?.() || Date.now().toString(36) + Math.random().toString(36).slice(2));

const TIPOS_BOTON = [
  { value: 'ninguno', label: 'Sin botón' },
  { value: 'voucher', label: 'Generar Voucher (PDF)' },
  { value: 'whatsapp', label: 'WhatsApp del prestador' },
  { value: 'email_beneficios', label: 'Mail a beneficios@colegiotslomas.org' },
  { value: 'web_prestador', label: 'Página web / link externo' },
];

const paqueteVacio = () => ({
  id: generarId(),
  titulo: '',
  imagen_url: '',
  descripcion_corta: '',
  descripcion_larga: '',
  precio: '',
  vigencia: '',
  boton_tipo: 'ninguno',
  boton_config: {},
});

export default function PaquetesTuristicosManager({ paquetes = [], onChange }) {
  const [expandidos, setExpandidos] = useState({});

  const toggleExpandir = (id) => {
    setExpandidos((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const agregarPaquete = () => {
    const nuevo = paqueteVacio();
    onChange([...(paquetes || []), nuevo]);
    setExpandidos((prev) => ({ ...prev, [nuevo.id]: true }));
  };

  const eliminarPaquete = (id) => {
    if (!window.confirm('¿Eliminar este paquete? Esta acción se aplicará al guardar el prestador.')) return;
    onChange(paquetes.filter((p) => p.id !== id));
  };

  const actualizarPaquete = (id, patch) => {
    onChange(paquetes.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  const actualizarBotonConfig = (id, patch) => {
    const p = paquetes.find((x) => x.id === id);
    if (!p) return;
    actualizarPaquete(id, { boton_config: { ...(p.boton_config || {}), ...patch } });
  };

  const moverPaquete = (index, direction) => {
    const nuevoIndex = index + direction;
    if (nuevoIndex < 0 || nuevoIndex >= paquetes.length) return;
    const copia = [...paquetes];
    [copia[index], copia[nuevoIndex]] = [copia[nuevoIndex], copia[index]];
    onChange(copia);
  };

  return (
    <div className="col-span-2 border-2 border-purple-200 bg-purple-50/30 rounded-xl p-5 mt-4" data-testid="paquetes-manager">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-purple-600" />
          <h3 className="font-bold text-gray-900">Paquetes Turísticos</h3>
          <span className="text-xs text-gray-500">
            ({paquetes.length} {paquetes.length === 1 ? 'paquete' : 'paquetes'})
          </span>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={agregarPaquete}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="agregar-paquete-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Agregar paquete
        </Button>
      </div>

      {paquetes.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-6">
          Todavía no hay paquetes cargados. Hacé click en <strong>Agregar paquete</strong> para crear el primero.
        </p>
      )}

      <div className="space-y-3">
        {paquetes.map((p, idx) => {
          const abierto = !!expandidos[p.id];
          return (
            <div
              key={p.id}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden"
              data-testid={`paquete-item-${idx}`}
            >
              {/* Header de la card (siempre visible) */}
              <div className="flex items-center gap-2 p-3 bg-gray-50">
                <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm truncate">
                    {p.titulo || <span className="text-gray-400 italic">Sin título</span>}
                  </p>
                  {(p.descripcion_corta || p.precio) && (
                    <p className="text-xs text-gray-500 truncate">
                      {p.descripcion_corta}
                      {p.descripcion_corta && p.precio && ' · '}
                      {p.precio && <strong>{p.precio}</strong>}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => moverPaquete(idx, -1)}
                  disabled={idx === 0}
                  className="p-1 h-7 w-7"
                  title="Mover arriba"
                >
                  <ArrowUp className="w-3 h-3" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => moverPaquete(idx, 1)}
                  disabled={idx === paquetes.length - 1}
                  className="p-1 h-7 w-7"
                  title="Mover abajo"
                >
                  <ArrowDown className="w-3 h-3" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => eliminarPaquete(p.id)}
                  className="p-1 h-7 w-7 text-red-600 hover:bg-red-50"
                  title="Eliminar paquete"
                  data-testid={`eliminar-paquete-${idx}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleExpandir(p.id)}
                  className="p-1 h-7 w-7"
                  data-testid={`expandir-paquete-${idx}`}
                >
                  {abierto ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </div>

              {/* Cuerpo del formulario (expandible) */}
              {abierto && (
                <div className="p-4 space-y-4 border-t border-gray-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor={`paq-titulo-${p.id}`}>Título del paquete *</Label>
                      <Input
                        id={`paq-titulo-${p.id}`}
                        value={p.titulo}
                        onChange={(e) => actualizarPaquete(p.id, { titulo: e.target.value })}
                        placeholder="Ej: Bariloche 7 noches - Hotel 4★"
                        data-testid={`paquete-titulo-${idx}`}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Label htmlFor={`paq-imagen-${p.id}`}>URL de imagen principal</Label>
                      <Input
                        id={`paq-imagen-${p.id}`}
                        value={p.imagen_url}
                        onChange={(e) => actualizarPaquete(p.id, { imagen_url: e.target.value })}
                        placeholder="https://..."
                        data-testid={`paquete-imagen-${idx}`}
                      />
                      {p.imagen_url && (
                        <img
                          src={p.imagen_url}
                          alt="preview"
                          className="mt-2 rounded-lg border border-gray-200 max-h-40 object-cover"
                        />
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <Label htmlFor={`paq-descorta-${p.id}`}>Descripción corta (subtítulo)</Label>
                      <Input
                        id={`paq-descorta-${p.id}`}
                        value={p.descripcion_corta}
                        onChange={(e) => actualizarPaquete(p.id, { descripcion_corta: e.target.value })}
                        placeholder="Ej: Aéreo + Hotel + Traslados + Desayuno"
                        data-testid={`paquete-desc-corta-${idx}`}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Label htmlFor={`paq-desclarga-${p.id}`}>
                        Descripción larga (Markdown)
                      </Label>
                      <Textarea
                        id={`paq-desclarga-${p.id}`}
                        value={p.descripcion_larga}
                        onChange={(e) => actualizarPaquete(p.id, { descripcion_larga: e.target.value })}
                        placeholder={`**Incluye:**\n- Aéreo ida y vuelta\n- 6 noches de hotel\n- Traslados in/out\n\n**No incluye:**\n- Excursiones opcionales`}
                        rows={6}
                        className="font-mono text-sm"
                        data-testid={`paquete-desc-larga-${idx}`}
                      />
                    </div>

                    <div>
                      <Label htmlFor={`paq-precio-${p.id}`}>Precio</Label>
                      <Input
                        id={`paq-precio-${p.id}`}
                        value={p.precio}
                        onChange={(e) => actualizarPaquete(p.id, { precio: e.target.value })}
                        placeholder="Ej: Desde USD 850 / persona"
                        data-testid={`paquete-precio-${idx}`}
                      />
                    </div>

                    <div>
                      <Label htmlFor={`paq-vigencia-${p.id}`}>Vigencia / Duración</Label>
                      <Input
                        id={`paq-vigencia-${p.id}`}
                        value={p.vigencia}
                        onChange={(e) => actualizarPaquete(p.id, { vigencia: e.target.value })}
                        placeholder="Ej: Vigente hasta 30/12/2026"
                        data-testid={`paquete-vigencia-${idx}`}
                      />
                    </div>
                  </div>

                  {/* Botón/CTA del paquete */}
                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-sm font-semibold text-gray-700 mb-3">Botón de acción (opcional)</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Tipo de botón</Label>
                        <Select
                          value={p.boton_tipo || 'ninguno'}
                          onValueChange={(v) =>
                            actualizarPaquete(p.id, { boton_tipo: v, boton_config: {} })
                          }
                        >
                          <SelectTrigger data-testid={`paquete-boton-tipo-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TIPOS_BOTON.map((t) => (
                              <SelectItem key={t.value} value={t.value}>
                                {t.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {p.boton_tipo === 'whatsapp' && (
                        <div>
                          <Label>Número de WhatsApp</Label>
                          <Input
                            value={p.boton_config?.numero || ''}
                            onChange={(e) => actualizarBotonConfig(p.id, { numero: e.target.value })}
                            placeholder="Ej: 5491112345678"
                          />
                        </div>
                      )}

                      {p.boton_tipo === 'web_prestador' && (
                        <div>
                          <Label>URL destino</Label>
                          <Input
                            value={p.boton_config?.url || ''}
                            onChange={(e) => actualizarBotonConfig(p.id, { url: e.target.value })}
                            placeholder="https://..."
                          />
                        </div>
                      )}
                    </div>

                    {p.boton_tipo === 'whatsapp' && (
                      <div className="mt-3">
                        <Label>Mensaje sugerido (opcional)</Label>
                        <Textarea
                          value={p.boton_config?.mensaje || ''}
                          onChange={(e) => actualizarBotonConfig(p.id, { mensaje: e.target.value })}
                          placeholder="Hola, me interesa el paquete X..."
                          rows={2}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
